
import Vue from "vue";
import Component from "vue-class-component";
import { namespace } from "vuex-class";
import { BoListPanel, IOnOffHandlers } from "bonomo/dist";
import { ELockerStatuses } from "@/model/Locker/lockerInterfaces";
import { BetterDataTableHeader } from "@/model/betterVuetifyInterfaces";
import LockerChip from "@/components/shared/chips/LockerChip.vue";
import ChannelChip from "@/components/shared/chips/ChannelChip.vue";
import SimpleConfirmationDialog from "@/components/shared/SimpleConfirmationDialog.vue";
import {PermissionHelper} from "@/helpers/PermissionHelper";
import {EUserPermissions} from "@/model/EUserPermissions";
import {IAuthUserData} from "@/store/modules/authModule";
import {IShortLocker} from "@/model/shortInterfaces";

const lockerModule = namespace("lockerModule");

const authModule = namespace("authModule");

enum FeatureToggle {
  DELIVERY_BY_PHONE = "delivery_by_phone",
  LOCKER_23_FEATURE = "locker23_feature",
  DIRECTORY_DELIVERY = "directory_delivery",
  DIRECTORY_DELIVERIES_24 = "directory_deliveries_24",
  DIRECTORY_DELIVERIES_25 = "directory_deliveries_25",
  SHOULD_SEND_REMINDERS = "should_send_reminders",
}

@Component({
  components: {
    BoListPanel,
    LockerChip,
    SimpleConfirmationDialog,
    ChannelChip,
  },
})
export default class LockerFeatures extends Vue {

  @authModule.Getter('userData')
  private authUser!: IAuthUserData;

  get canEdit(): boolean{
    return PermissionHelper.hasPermission(this.authUser, EUserPermissions.PERMISSION_INSTALLER, 2);
  }

  @lockerModule.Getter("lockers")
  private lockers!: IShortLocker[];

  @lockerModule.Action("fetchLockers")
  private fetchLockers!: () => void;

  @lockerModule.Action('setFeature')
  private setFeature!: (params: { lockerId: number, data: object }) => Promise<any>;

  protected search: string = "";
  protected loading: boolean = true;
  private showConfirmationDialog: boolean = false;
  private toggledLocker: IShortLocker | null = null;
  private togglesDisabled: boolean = false;
  private toggledFeature: FeatureToggle = FeatureToggle.DIRECTORY_DELIVERY;
  toggledFeatureValue: boolean = false;

  private subscribes: IOnOffHandlers = {};

  created() {
    this.subscribes = this.$ons({
      'backend.call.OK.lockerToggleLoaded': this.onBackendEvent,
      'backend.call.KO.lockerToggleLoaded': this.onBackendEvent,
    })
  }


  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  protected get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('common.id'), align: 'start', sortable: true, value: 'id' },
      { text: this.$t("common.name"), align: "start", value: "name" },
      { text: this.$t('channels.title'), align: 'start', value: 'channel_id' },
      { text: this.$t("lockers.locker.fields.locker23_feature.title"),  align: "center", value: "locker23_feature" },
      { text: this.$t("lockers.locker.fields.delivery_by_phone.title"),  align: "center", value: "delivery_by_phone" },
      { text: this.$t("lockers.locker.fields.directory_delivery.title"), align: "center", value: "directory_delivery" },
      { text: this.$t("lockers.locker.fields.directory_deliveries_24.title"), align: "center", value: "directory_deliveries_24" },
      { text: this.$t("lockers.locker.fields.directory_deliveries_25.title"), align: "center", value: "directory_deliveries_25" },
      { text: this.$t("lockers.locker.fields.should_send_reminders.title"), align: "center", value: "should_send_reminders" },
    ];
  }

  protected get items() {
    let ret = this.lockers.filter(each => each.status !== ELockerStatuses.Deleted);
    if (this.search.length) {
      ret = ret.filter(each => this.searchString(each).includes(this.search.toLowerCase()))
    }
    return ret;
  }

  private searchString(locker: IShortLocker): string {
    return ('' +
      locker.id + ' ' +
      locker.name + ' ' +
      this.channelName(locker))
      .toLowerCase();
  }

  private channelName(locker: IShortLocker): string {
    return ''+this.$t(`channels.names.${ locker.channel_id }`);
  }

  protected loadData(initial?: boolean) {
    this.fetchLockers();
  }

  onChangeDeliveryByPhone(item: IShortLocker) {
    this.toggledLocker = item;
    this.toggledFeature =  FeatureToggle.DELIVERY_BY_PHONE;
    this.toggledFeatureValue = item.delivery_by_phone;
    this.showConfirmationDialog = true;
  }

  private onChangeDirectoryDelivery(item: IShortLocker) {
    this.toggledLocker = item;
    this.toggledFeature =  FeatureToggle.DIRECTORY_DELIVERY;
    this.toggledFeatureValue = item.directory_delivery;
    this.showConfirmationDialog = true;
  }

  onChangeDirectoryDeliveries24(item: IShortLocker) {
    this.toggledLocker = item;
    this.toggledFeature =  FeatureToggle.DIRECTORY_DELIVERIES_24;
    this.toggledFeatureValue = item.directory_deliveries_24;
    this.showConfirmationDialog = true;
  }

  onChangeDirectoryDeliveries25(item: IShortLocker) {
    this.toggledLocker = item;
    this.toggledFeature =  FeatureToggle.DIRECTORY_DELIVERIES_25;
    this.toggledFeatureValue = item.directory_deliveries_25;
    this.showConfirmationDialog = true;
  }

  protected onChangeLocker23Feature(item: IShortLocker) {
    this.toggledLocker = item;
    this.toggledFeature =  FeatureToggle.LOCKER_23_FEATURE;
    this.toggledFeatureValue = item.locker23_feature;
    this.showConfirmationDialog = true;
  }

  protected onChangeShouldSendReminders(item: IShortLocker) {
    this.toggledLocker = item;
    this.toggledFeature =  FeatureToggle.SHOULD_SEND_REMINDERS;
    this.toggledFeatureValue = item.should_send_reminders;
    this.showConfirmationDialog = true;
  }

  protected onSubmit() {
    // we'll always have a currentItem here but we need the if to keep lint happy
    if (this.toggledLocker) {

      this.togglesDisabled = true;

      switch(this.toggledFeature) {
        case FeatureToggle.DELIVERY_BY_PHONE:
          this.setFeature({
            lockerId: this.toggledLocker.id,
            data: {delivery_by_phone: !this.toggledLocker.delivery_by_phone,},
          });
          break;
        case FeatureToggle.DIRECTORY_DELIVERY:
          this.setFeature({
            lockerId: this.toggledLocker.id,
            data: {directory_delivery: !this.toggledLocker.directory_delivery,},
          });
          this.showConfirmationDialog = false;
          break;
        case FeatureToggle.DIRECTORY_DELIVERIES_24:
          this.setFeature({
            lockerId: this.toggledLocker.id,
            data: {directory_deliveries_24: !this.toggledLocker.directory_deliveries_24,},
          });
          this.showConfirmationDialog = false;
          break;
        case FeatureToggle.DIRECTORY_DELIVERIES_25:
          this.setFeature({
            lockerId: this.toggledLocker.id,
            data: {directory_deliveries_25: !this.toggledLocker.directory_deliveries_25,},
          });
          this.showConfirmationDialog = false;
          break;
        case FeatureToggle.LOCKER_23_FEATURE:
          this.setFeature({
            lockerId: this.toggledLocker.id,
            data: {locker23_feature: !this.toggledLocker.locker23_feature,},
          });
          break;
        case FeatureToggle.SHOULD_SEND_REMINDERS:
          this.setFeature({
            lockerId: this.toggledLocker.id,
            data: {should_send_reminders: !this.toggledLocker.should_send_reminders,},
          });
          this.showConfirmationDialog = false;
          break;
      }

      this.showConfirmationDialog = false;

    }
  }

  private onBackendEvent() {
    this.togglesDisabled = false;
  }

}
